import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  FolderOpen,
  Settings,
  LogOut,
  Copy,
  Link2,
  Loader2,
  Moon,
  Sun,
  Sparkles,
  AlertTriangle,
} from 'lucide-react'
import { useAppStore } from '../stores/useAppStore'
import { getMyRooms, createInvitation, leaveRoom, getMyWorkspaces, createWorkspace, createRoom, updateRoom, deleteRoom } from '../services/rooms'
import type { Room, RoomStudioSnapshot, Workspace } from '../services/rooms'
import { api, logout } from '../services/api'
import Modal from '../components/ui/Modal'
import CommandPalette, { type CommandItem } from '../components/CommandPalette'
import { formatRelative, PIPELINE_STAGES, roleLabel, workspaceColor, type RoomPipeline } from '../lib/pipeline'
import { Search, CircleAlert, Bot, LayoutGrid, Shapes, KeyRound } from 'lucide-react'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import { useToast } from '../components/ui/Toast'
import { PERSONAS } from '../lib/personas'
import BrandMark from '../components/BrandMark'
import ApiKeysModal from '../components/ApiKeysModal'

export default function DashboardPage() {
  const navigate = useNavigate()
  const { user, darkMode, toggleDarkMode } = useAppStore()
  const { toast } = useToast()
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(true)
  const [keysOpen, setKeysOpen] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [createOpen, setCreateOpen] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [newRoomName, setNewRoomName] = useState('')
  const [newRoomDesc, setNewRoomDesc] = useState('')
  const [newRoomType, setNewRoomType] = useState<'whiteboard' | 'studio'>('whiteboard')
  const [generatingTitle, setGeneratingTitle] = useState(false)
  const [newRoomWorkspace, setNewRoomWorkspace] = useState('')
  const [newWorkspaceName, setNewWorkspaceName] = useState('')
  const [showNewWorkspace, setShowNewWorkspace] = useState(false)
  const [inviteRoomId, setInviteRoomId] = useState<string | null>(null)
  const [inviteToken, setInviteToken] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [settingsRoom, setSettingsRoom] = useState<Room | null>(null)
  const [settingsName, setSettingsName] = useState('')
  const [settingsDesc, setSettingsDesc] = useState('')
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false)
  const [leaveTarget, setLeaveTarget] = useState<Room | null>(null)
  const [section, setSection] = useState<'projects' | 'inbox' | 'agents'>('projects')
  const [wsFilter, setWsFilter] = useState<string | null>(null)
  const [dashCmd, setDashCmd] = useState(false)
  const [leaveLoading, setLeaveLoading] = useState(false)

  useEffect(() => {
    loadRooms()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setDashCmd((open) => !open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const loadRooms = async () => {
    try {
      setLoading(true)
      setLoadError(false)
      const [roomsData, workspacesData] = await Promise.all([
        getMyRooms(),
        getMyWorkspaces(),
      ])
      setRooms(roomsData)
      setWorkspaces(workspacesData)
      if (workspacesData.length === 1) {
        setNewRoomWorkspace(workspacesData[0].id)
      }
    } catch (err) {
      console.error('Erro ao carregar salas:', err)
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }

  const handleEnterRoom = (room: Room) => {
    localStorage.setItem('currentRoom', JSON.stringify(room))
    useAppStore.setState({ currentRoom: room })
    navigate(`/?room=${room.id}`)
  }

  const handleGenerateTitle = async () => {
    if (!newRoomDesc.trim()) return
    try {
      setGeneratingTitle(true)
      const res = await api.post('/api/v1/agent/generate-title', {
        description: newRoomDesc.trim(),
      })
      setNewRoomName(res.data.title)
    } catch (err: any) {
      console.error('Erro ao gerar título:', err)
      toast({
        variant: 'error',
        title: 'Não foi possível gerar o título',
        description: err.response?.data?.detail || 'Tente novamente ou digite um nome manualmente.',
      })
    } finally {
      setGeneratingTitle(false)
    }
  }

  const closeCreateModal = () => {
    setCreateOpen(false)
    setNewRoomName('')
    setNewRoomDesc('')
    setNewRoomType('whiteboard')
    setShowNewWorkspace(false)
    setNewWorkspaceName('')
  }

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return
    try {
      setCreateLoading(true)
      let workspaceId = newRoomWorkspace

      if (showNewWorkspace && newWorkspaceName.trim()) {
        const ws = await createWorkspace({ name: newWorkspaceName.trim() })
        workspaceId = ws.id
      }

      if (!workspaceId) {
        toast({
          variant: 'error',
          title: 'Workspace obrigatório',
          description: 'Selecione um workspace ou crie um novo para o projeto.',
        })
        return
      }

      const room = await createRoom({
        workspace_id: workspaceId,
        name: newRoomName.trim(),
        description: newRoomDesc.trim() || undefined,
        room_type: newRoomType,
      })

      setRooms((prev) => [room, ...prev])
      closeCreateModal()
      toast({ variant: 'success', title: 'Projeto criado' })
    } catch (err: any) {
      toast({
        variant: 'error',
        title: 'Erro ao criar projeto',
        description: err.response?.data?.detail,
      })
    } finally {
      setCreateLoading(false)
    }
  }

  const handleInvite = async (roomId: string) => {
    setInviteRoomId(roomId)
    setInviteToken('')
    setCopied(false)
    try {
      setInviteLoading(true)
      const inv = await createInvitation(roomId)
      const baseUrl = window.location.origin
      setInviteToken(`${baseUrl}/join/${inv.token}`)
    } catch (err: any) {
      toast({
        variant: 'error',
        title: 'Erro ao gerar convite',
        description: err.response?.data?.detail,
      })
    } finally {
      setInviteLoading(false)
    }
  }

  const handleCopyInvite = async () => {
    if (!inviteToken) return
    try {
      await navigator.clipboard.writeText(inviteToken)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback
      const ta = document.createElement('textarea')
      ta.value = inviteToken
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleConfirmLeave = async () => {
    if (!leaveTarget) return
    try {
      setLeaveLoading(true)
      await leaveRoom(leaveTarget.id)
      setRooms((prev) => prev.filter((r) => r.id !== leaveTarget.id))
      setLeaveTarget(null)
      toast({ variant: 'success', title: 'Você saiu do projeto' })
    } catch (err: any) {
      toast({
        variant: 'error',
        title: 'Erro ao sair do projeto',
        description: err.response?.data?.detail,
      })
    } finally {
      setLeaveLoading(false)
    }
  }

  const handleOpenSettings = (room: Room) => {
    setSettingsRoom(room)
    setSettingsName(room.name)
    setSettingsDesc(room.description || '')
  }

  const handleSaveSettings = async () => {
    if (!settingsRoom) return
    try {
      setSettingsLoading(true)
      const updated = await updateRoom(settingsRoom.id, {
        name: settingsName.trim(),
        description: settingsDesc.trim() || undefined,
      })
      setRooms((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated, my_role: r.my_role, workspace_name: r.workspace_name } : r)))
      setSettingsRoom(null)
      toast({ variant: 'success', title: 'Alterações salvas' })
    } catch (err: any) {
      toast({
        variant: 'error',
        title: 'Erro ao salvar alterações',
        description: err.response?.data?.detail,
      })
    } finally {
      setSettingsLoading(false)
    }
  }

  const handleConfirmArchive = async () => {
    if (!settingsRoom) return
    try {
      setSettingsLoading(true)
      await deleteRoom(settingsRoom.id)
      setRooms((prev) => prev.filter((r) => r.id !== settingsRoom.id))
      setArchiveConfirmOpen(false)
      setSettingsRoom(null)
      toast({ variant: 'success', title: 'Projeto arquivado' })
    } catch (err: any) {
      toast({
        variant: 'error',
        title: 'Erro ao arquivar projeto',
        description: err.response?.data?.detail,
      })
      setArchiveConfirmOpen(false)
    } finally {
      setSettingsLoading(false)
    }
  }

  const inbox = rooms.filter((room) => Boolean((room.settings?.pipeline as RoomPipeline | undefined)?.pending_hitl))
  const visibleRooms = rooms.filter((room) => {
    if (section === 'inbox') return Boolean((room.settings?.pipeline as RoomPipeline | undefined)?.pending_hitl)
    if (wsFilter) return room.workspace_name === wsFilter
    return true
  })
  const workspaceNames = Array.from(new Set(rooms.map((room) => room.workspace_name).filter(Boolean))) as string[]

  const dashCommands: CommandItem[] = [
    { id: 'new', label: 'Novo projeto', icon: Plus, run: () => setCreateOpen(true) },
    { id: 'inbox', label: 'Precisa de você', icon: CircleAlert, run: () => setSection('inbox') },
    { id: 'all', label: 'Todos os projetos', icon: LayoutGrid, run: () => { setSection('projects'); setWsFilter(null) } },
    { id: 'agents', label: 'Agentes', icon: Bot, run: () => setSection('agents') },
    { id: 'theme', label: 'Alternar tema', icon: darkMode ? Sun : Moon, run: toggleDarkMode },
    ...rooms.slice(0, 8).map((room) => ({
      id: room.id,
      label: room.name,
      hint: room.workspace_name || '',
      icon: FolderOpen,
      run: () => handleEnterRoom(room),
    })),
  ]

  return (
    <div className="h-screen flex bg-canvas text-ink">
      <aside className="w-[220px] shrink-0 border-r border-line bg-surface flex flex-col p-3 gap-4">
        <div className="flex items-center gap-2 px-1.5">
          <BrandMark size={22} />
          <span className="font-semibold">ArchForge</span>
        </div>
        <nav className="flex flex-col gap-0.5">
          <button type="button" onClick={() => { setSection('projects'); setWsFilter(null) }} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-left cursor-pointer ${section === 'projects' && !wsFilter ? 'bg-surface-2 font-medium' : 'text-muted'}`}>
            <LayoutGrid size={14} /> Projetos <span className="ml-auto font-mono text-[11px] text-faint">{rooms.length}</span>
          </button>
          <button type="button" onClick={() => setSection('inbox')} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-left cursor-pointer ${section === 'inbox' ? 'bg-surface-2 font-medium' : 'text-muted'}`}>
            <CircleAlert size={14} /> Precisa de você
            {inbox.length > 0 && <span className="ml-auto font-mono text-[10px] font-semibold text-warn bg-warn-soft px-1.5 rounded-full">{inbox.length}</span>}
          </button>
          <button type="button" onClick={() => setSection('agents')} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-left cursor-pointer ${section === 'agents' ? 'bg-surface-2 font-medium' : 'text-muted'}`}>
            <Bot size={14} /> Agentes <span className="ml-auto font-mono text-[11px] text-faint">4</span>
          </button>
        </nav>
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint px-2 pb-1">Workspaces</span>
          {workspaceNames.map((name) => (
            <button key={name} type="button" onClick={() => { setSection('projects'); setWsFilter(name) }} className={`flex items-center gap-2 px-2 py-1 rounded-md text-[12.5px] text-left cursor-pointer ${wsFilter === name ? 'bg-surface-2 text-ink' : 'text-muted'}`}>
              <span className="w-2 h-2 rounded-sm" style={{ background: workspaceColor(name) }} />
              <span className="truncate flex-1">{name}</span>
              <span className="font-mono text-[11px] text-faint">{rooms.filter((room) => room.workspace_name === name).length}</span>
            </button>
          ))}
        </div>
        <div className="mt-auto flex items-center gap-2 px-1.5 pt-3 border-t border-line">
          <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[11px] font-semibold flex items-center justify-center">{user?.name?.charAt(0).toUpperCase()}</div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">{user?.name}</div>
          </div>
          <button type="button" onClick={() => setKeysOpen(true)} className="w-7 h-7 rounded-md text-muted cursor-pointer" aria-label="Chaves de API (MCP)" title="Chaves de API (MCP): acesse seus projetos pelo Claude, Cursor e outros agentes">
            <KeyRound size={14} className="mx-auto" />
          </button>
          <button type="button" onClick={toggleDarkMode} className="w-7 h-7 rounded-md text-muted cursor-pointer" aria-label={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}>
            {darkMode ? <Sun size={14} className="mx-auto" /> : <Moon size={14} className="mx-auto" />}
          </button>
          <button type="button" onClick={logout} className="w-7 h-7 rounded-md text-muted cursor-pointer" aria-label="Sair da conta">
            <LogOut size={14} className="mx-auto" />
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-auto p-6 flex flex-col gap-5">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold m-0">{section === 'inbox' ? 'Precisa de você' : section === 'agents' ? 'Agentes' : 'Projetos'}</h1>
          <div className="flex-1" />
          <button type="button" onClick={() => setDashCmd(true)} className="h-8 min-w-[220px] flex items-center gap-2 px-2.5 border border-line rounded-lg bg-surface text-faint text-[12.5px] cursor-pointer">
            <Search size={13} /> Buscar projetos e comandos <span className="ml-auto font-mono text-[11px] border border-line rounded px-1">⌘K</span>
          </button>
          <button type="button" onClick={() => setCreateOpen(true)} className="h-8 px-3 rounded-lg bg-ink text-surface text-[12.5px] font-semibold inline-flex items-center gap-1.5 cursor-pointer">
            <Plus size={14} /> Novo projeto
          </button>
        </div>

        {section === 'inbox' && (
          <p className="text-sm text-muted m-0">Agentes pausados aguardando uma decisão humana.</p>
        )}

        {section === 'agents' ? (
          <section className="grid sm:grid-cols-2 gap-3 max-w-3xl">
            {PERSONAS.map((persona) => (
              <article key={persona.id} className="bg-surface border border-line rounded-xl p-4">
                <div className="w-9 h-9 rounded-lg text-white flex items-center justify-center" style={{ background: persona.color }}>
                  <persona.icon size={18} />
                </div>
                <h2 className="mt-3 text-base font-semibold m-0">{persona.name}</h2>
                <p className="text-sm text-muted mt-0.5 mb-2">{persona.role}</p>
                <p className="text-sm m-0 leading-relaxed">{persona.produces}</p>
              </article>
            ))}
          </section>
        ) : loading ? (
          <div className="flex items-center justify-center py-20" role="status"><Loader2 size={24} className="animate-spin text-accent" /></div>
        ) : loadError ? (
          <div className="text-center py-16 bg-surface border border-line rounded-xl">
            <AlertTriangle size={40} className="mx-auto text-warn mb-3" />
            <p className="font-medium">Não foi possível carregar seus projetos.</p>
            <button type="button" onClick={loadRooms} className="mt-3 px-3 py-1.5 rounded-lg bg-ink text-surface text-sm cursor-pointer">Tentar novamente</button>
          </div>
        ) : visibleRooms.length === 0 ? (
          <div className="text-center py-16 bg-surface border border-line rounded-xl">
            <FolderOpen size={40} className="mx-auto text-faint mb-3" />
            <p className="font-medium">{section === 'inbox' ? 'Nenhuma decisão pendente.' : 'Nenhum projeto ainda.'}</p>
          </div>
        ) : (
          <section className="bg-surface border border-line rounded-[10px] overflow-hidden">
            <div className="grid grid-cols-[minmax(180px,2fr)_minmax(140px,1.2fr)_minmax(140px,1.4fr)_90px_88px] gap-3 px-3.5 py-2 border-b border-line font-mono text-[10.5px] uppercase tracking-wide text-faint">
              <span>Projeto</span><span>Pipeline</span><span>Agora</span><span>Papel</span><span className="text-right">Atualizado</span>
            </div>
            {visibleRooms.map((room) => {
              const pipeline = room.settings?.pipeline as RoomPipeline | undefined
              const segments = pipeline?.segments?.length === 5 ? pipeline.segments : [0, 0, 0, 0, 0]
              const isStudio = room.room_type === 'studio'
              const studio = room.settings?.studio as RoomStudioSnapshot | undefined
              return (
                <div key={room.id} className="grid grid-cols-[minmax(180px,2fr)_minmax(140px,1.2fr)_minmax(140px,1.4fr)_90px_88px] gap-3 px-3.5 py-2.5 border-b border-line-2 items-center hover:bg-surface-2">
                  <button type="button" onClick={() => handleEnterRoom(room)} className="text-left min-w-0 cursor-pointer">
                    <div className="font-semibold truncate">{room.name}</div>
                    <div className="text-[11.5px] text-faint truncate">{room.workspace_name || 'Workspace'} · {roleLabel(room.my_role)}</div>
                  </button>
                  {isStudio ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent-soft text-accent inline-flex items-center gap-1">
                        <Shapes size={11} /> Studio
                      </span>
                      <span className="font-mono text-[11px] text-muted whitespace-nowrap">{studio ? `${studio.pages} pág.` : '—'}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 min-w-0">
                      {PIPELINE_STAGES.map((label, index) => (
                        <div key={label} title={label} className="flex-1 max-w-8 h-[5px] rounded-sm" style={{ background: segments[index] === 2 ? 'var(--af-warn)' : segments[index] ? 'var(--af-acc)' : 'var(--af-ln)' }} />
                      ))}
                      <span className="font-mono text-[11px] text-muted whitespace-nowrap">{pipeline?.stage_label || '—'}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => !isStudio && pipeline?.pending_hitl ? navigate(`/?room=${room.id}&decision=1`) : handleEnterRoom(room)}
                    className="flex items-center gap-1.5 text-xs text-muted text-left min-w-0 cursor-pointer"
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: !isStudio && pipeline?.pending_hitl ? 'var(--af-warn)' : 'var(--af-fnt)' }} />
                    <span className="truncate">
                      {isStudio
                        ? studio?.now
                          ? `${studio.now}${studio.last_agent ? ` · ${studio.last_agent.split(' (')[0]}` : ''}`
                          : 'Diagrama vazio'
                        : pipeline?.now || 'Sem geração ainda'}
                    </span>
                  </button>
                  <span className="text-[11.5px] text-faint">{roleLabel(room.my_role)}</span>
                  <div className="flex items-center justify-end gap-1">
                    <span className="font-mono text-[11.5px] text-faint">{formatRelative((isStudio ? studio?.updated_at : pipeline?.updated_at) || room.updated_at)}</span>
                    {(room.my_role === 'owner' || room.my_role === 'admin') && (
                      <button type="button" onClick={() => handleInvite(room.id)} className="p-1 text-muted cursor-pointer" aria-label={`Convidar para ${room.name}`}><Link2 size={13} /></button>
                    )}
                    {room.my_role === 'owner' && (
                      <button type="button" onClick={() => handleOpenSettings(room)} className="p-1 text-muted cursor-pointer" aria-label={`Configurações de ${room.name}`}><Settings size={13} /></button>
                    )}
                    {room.my_role !== 'owner' && (
                      <button type="button" onClick={() => setLeaveTarget(room)} className="p-1 text-muted cursor-pointer" aria-label={`Sair de ${room.name}`}><LogOut size={13} /></button>
                    )}
                  </div>
                </div>
              )
            })}
          </section>
        )}
      </main>
      <CommandPalette open={dashCmd} onClose={() => setDashCmd(false)} commands={dashCommands} />

      {/* Create Room Modal */}
      <Modal
        open={createOpen}
        onClose={closeCreateModal}
        title="Novo Projeto"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={closeCreateModal}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateRoom}
              disabled={createLoading || !newRoomName.trim() || (!newRoomWorkspace && !(showNewWorkspace && newWorkspaceName.trim()))}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-agent text-white text-sm font-medium hover:bg-agent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {createLoading && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
              Criar Projeto
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Como você quer trabalhar?</legend>
            <div className="grid grid-cols-2 gap-2">
              {([
                ['whiteboard', 'Ideias → documentação', 'Post-its no canvas; os agentes geram SDD, Event Storm, diagramas e protótipo.', LayoutGrid],
                ['studio', 'Studio de diagramas', 'Converse com os agentes para desenhar um diagrama editável, como um draw.io.', Shapes],
              ] as const).map(([value, label, hint, Icon]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setNewRoomType(value)}
                  aria-pressed={newRoomType === value}
                  className="text-left p-3 rounded-lg border cursor-pointer flex flex-col gap-1"
                  style={{
                    borderColor: newRoomType === value ? 'var(--af-acc)' : 'var(--af-ln)',
                    background: newRoomType === value ? 'var(--af-accs)' : 'transparent',
                  }}
                >
                  <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                    <Icon size={14} className="text-accent" /> {label}
                  </span>
                  <span className="text-xs text-muted leading-snug">{hint}</span>
                </button>
              ))}
            </div>
          </fieldset>
          <div>
            <label
              htmlFor="new-room-desc"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Descrição do projeto *
            </label>
            <textarea
              id="new-room-desc"
              data-autofocus
              value={newRoomDesc}
              onChange={(e) => setNewRoomDesc(e.target.value)}
              placeholder="Descreva o projeto em poucas palavras..."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent resize-none placeholder:text-gray-400"
            />
          </div>

          <div>
            <label
              htmlFor="new-room-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Nome do projeto *
            </label>
            <div className="flex items-center gap-2">
              <input
                id="new-room-name"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="Ex: Arquitetura do Sistema X"
                className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent placeholder:text-gray-400"
              />
              <button
                onClick={handleGenerateTitle}
                disabled={generatingTitle || !newRoomDesc.trim()}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-sm font-medium hover:bg-violet-200 dark:hover:bg-violet-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                title="Gerar título com IA"
              >
                {generatingTitle ? (
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                ) : (
                  <Sparkles size={14} aria-hidden="true" />
                )}
                Gerar
              </button>
            </div>
          </div>

          {!showNewWorkspace ? (
            <div>
              <label
                htmlFor="new-room-workspace"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Workspace *
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {workspaces.map((ws) => (
                  <button
                    key={ws.id}
                    type="button"
                    onClick={() => setNewRoomWorkspace(ws.id)}
                    className="h-7 inline-flex items-center gap-1.5 px-2.5 rounded-md border text-xs cursor-pointer"
                    style={{
                      borderColor: newRoomWorkspace === ws.id ? 'var(--af-ink)' : 'var(--af-ln)',
                      background: newRoomWorkspace === ws.id ? 'var(--af-sf2)' : 'transparent',
                    }}
                  >
                    <span className="w-2 h-2 rounded-sm" style={{ background: workspaceColor(ws.name) }} />
                    {ws.name}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setShowNewWorkspace(true)
                  setNewRoomWorkspace('')
                }}
                className="mt-2 text-xs text-agent hover:underline cursor-pointer"
              >
                + Criar novo workspace
              </button>
            </div>
          ) : (
            <div>
              <label
                htmlFor="new-workspace-name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Novo Workspace *
              </label>
              <input
                id="new-workspace-name"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="Nome do workspace"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent placeholder:text-gray-400"
              />
              <button
                onClick={() => {
                  setShowNewWorkspace(false)
                  if (workspaces.length === 1) setNewRoomWorkspace(workspaces[0].id)
                }}
                className="mt-2 text-xs text-gray-500 hover:underline cursor-pointer"
              >
                ← Usar workspace existente
              </button>
            </div>
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500">* obrigatório</p>
        </div>
      </Modal>

      {/* Settings Modal */}
      <Modal
        open={!!settingsRoom}
        onClose={() => setSettingsRoom(null)}
        title="Configurações do Projeto"
        size="md"
        dismissable={!settingsLoading}
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setSettingsRoom(null)}
              disabled={settingsLoading}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={settingsLoading || !settingsName.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-agent text-white text-sm font-medium hover:bg-agent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {settingsLoading && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
              Salvar
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="settings-room-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Nome
            </label>
            <input
              id="settings-room-name"
              data-autofocus
              value={settingsName}
              onChange={(e) => setSettingsName(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent"
            />
          </div>

          <div>
            <label
              htmlFor="settings-room-desc"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Descrição
            </label>
            <textarea
              id="settings-room-desc"
              value={settingsDesc}
              onChange={(e) => setSettingsDesc(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent resize-none"
            />
          </div>

          <div className="pt-2 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={() => setArchiveConfirmOpen(true)}
              disabled={settingsLoading}
              className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors cursor-pointer disabled:opacity-50"
            >
              <LogOut size={14} aria-hidden="true" />
              Arquivar projeto
            </button>
          </div>
        </div>
      </Modal>

      {/* Invite Modal */}
      <Modal
        open={!!inviteRoomId}
        onClose={() => {
          setInviteRoomId(null)
          setInviteToken('')
          setCopied(false)
        }}
        title="Link de Convite"
        size="md"
        footer={
          <div className="flex justify-end">
            <button
              onClick={() => {
                setInviteRoomId(null)
                setInviteToken('')
                setCopied(false)
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              Fechar
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Compartilhe este link para convidar pessoas para o projeto.
        </p>

        {inviteLoading ? (
          <div className="flex items-center justify-center py-6" role="status" aria-label="Gerando convite">
            <Loader2 size={20} className="animate-spin text-agent" aria-hidden="true" />
          </div>
        ) : inviteToken ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                readOnly
                aria-label="Link de convite"
                value={inviteToken}
                className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200 outline-none"
              />
              <button
                onClick={handleCopyInvite}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-agent text-white text-sm font-medium hover:bg-agent-dark transition-colors cursor-pointer"
              >
                <Copy size={14} aria-hidden="true" />
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              O link expira em 7 dias e pode ser usado uma única vez.
            </p>
          </div>
        ) : (
          <p className="text-sm text-red-500">Erro ao gerar convite.</p>
        )}
      </Modal>

      {/* Confirmação: sair do projeto */}
      <ConfirmDialog
        open={!!leaveTarget}
        title="Sair do projeto"
        variant="danger"
        confirmLabel="Sair do projeto"
        loading={leaveLoading}
        onConfirm={handleConfirmLeave}
        onCancel={() => setLeaveTarget(null)}
      >
        Você perderá acesso ao projeto{' '}
        <strong className="text-gray-900 dark:text-gray-100">{leaveTarget?.name}</strong> e
        precisará de um novo convite para voltar a participar.
      </ConfirmDialog>

      {/* Confirmação: arquivar projeto */}
      <ConfirmDialog
        open={archiveConfirmOpen}
        title="Arquivar projeto"
        variant="danger"
        confirmLabel="Arquivar"
        loading={settingsLoading}
        onConfirm={handleConfirmArchive}
        onCancel={() => setArchiveConfirmOpen(false)}
      >
        O projeto{' '}
        <strong className="text-gray-900 dark:text-gray-100">{settingsRoom?.name}</strong> será
        arquivado e deixará de aparecer para todos os membros. Esta ação não pode ser desfeita.
      </ConfirmDialog>

      <ApiKeysModal open={keysOpen} onClose={() => setKeysOpen(false)} />
    </div>
  )
}
